import type { OpenItemEditorParams } from '@/hooks/useDialogHost';
import { getInjector } from '@/services/angularjs-bridge';

export type BrowseRouteMode = 'browse' | 'edit' | 'copy';

export interface BrowseRoute {
    routeMode: BrowseRouteMode;
    primaryKey?: string;
    section?: string;
    attribute?: string;
}

export interface BrowsePageRouteProps {
    routeMode?: BrowseRouteMode;
    primaryKey?: string;
    section?: string;
    attribute?: string;
}

/**
 * `displayUrl` changes the path without remounting the route, so
 * `$routeParams` stay on the values from the first match. Read the live
 * path instead. A primary key may contain slashes; `section` / `attribute`
 * are the last marker before the focus name.
 */
export function parseBrowseLocation(path: string): BrowseRoute {
    const parts = (path || '').split('/').filter((part) => part.length > 0);
    const head = parts[0];
    if (head !== 'edit' && head !== 'copy') {
        return { routeMode: 'browse' };
    }

    const rest = parts.slice(1);
    const sectionAt = rest.lastIndexOf('section');
    const attributeAt = rest.lastIndexOf('attribute');
    let primaryParts = rest;
    let section: string | undefined;
    let attribute: string | undefined;

    if (sectionAt > 0 && sectionAt < rest.length - 1) {
        section = rest[sectionAt + 1];
        primaryParts = rest.slice(0, sectionAt);
    } else if (attributeAt > 0 && attributeAt < rest.length - 1) {
        attribute = rest[attributeAt + 1];
        primaryParts = rest.slice(0, attributeAt);
    }

    const primaryKey = primaryParts.join('/') || undefined;
    return { routeMode: head, primaryKey, section, attribute };
}

export function routeOpensEditor(route: BrowseRoute): boolean {
    if (route.routeMode === 'edit') {
        return true;
    }
    return route.routeMode === 'copy' && !!route.primaryKey;
}

export function editorParamsForRoute(route: BrowseRoute): OpenItemEditorParams {
    const isCopy = route.routeMode === 'copy';
    const isNew = route.routeMode === 'edit' && (!route.primaryKey || route.primaryKey === 'new');
    return {
        editorState: undefined,
        forceCloseTask: undefined,
        originalPrimaryKey: isCopy ? (route.primaryKey ?? '') : '',
        primaryKey: isCopy || isNew ? '' : (route.primaryKey ?? ''),
        task: '',
        items: undefined,
    };
}

/**
 * Closing the editor from the live list must keep the browse route mounted.
 * A real `/edit` or `/copy` deep link has to switch routes so `$routeParams`
 * drop the primary key. `/edit` and `/edit/new` stay in place: that is how a
 * new item opened from the list avoids a remount.
 */
export function keepBrowseShellOnEditorClose(originalPath: string | undefined, currentPath: string): boolean {
    if (originalPath === '/browse') {
        return true;
    }
    return currentPath === '/edit' || currentPath.startsWith('/edit/new');
}

export function routeFromProps(props: BrowsePageRouteProps): BrowseRoute {
    return {
        routeMode: props.routeMode ?? 'browse',
        primaryKey: props.primaryKey,
        section: props.section,
        attribute: props.attribute,
    };
}

export function readBrowseRoute(fallback: BrowseRoute): BrowseRoute {
    try {
        const path = getInjector().get('$location').path();
        if (typeof path === 'string' && path.length > 0) {
            return parseBrowseLocation(path);
        }
    } catch {
        // Storybook and unit tests have no Angular injector.
    }
    return fallback;
}

type SearchParams = Record<string, unknown>;

let searchWhenEditorOpened: SearchParams | null = null;

/** True when every key in either query has the same value. Missing and null match. */
export function browseQueriesMatch(current: SearchParams, snapshot: SearchParams): boolean {
    const keys = new Set([...Object.keys(current), ...Object.keys(snapshot)]);
    for (const key of keys) {
        if ((current[key] ?? null) !== (snapshot[key] ?? null)) {
            return false;
        }
    }
    return true;
}

/** Search writes that put `current` back to `snapshot`. A missing snapshot key is cleared. */
export function browseSearchUpdates(
    current: SearchParams,
    snapshot: SearchParams,
): Array<[string, unknown]> {
    const keys = new Set([...Object.keys(current), ...Object.keys(snapshot)]);
    const updates: Array<[string, unknown]> = [];
    for (const key of keys) {
        const next = snapshot[key] ?? null;
        const prev = current[key] ?? null;
        if (next !== prev) {
            updates.push([key, next]);
        }
    }
    return updates;
}

function readLocationSearch(search: () => SearchParams | undefined): SearchParams {
    const value = search();
    return value && typeof value === 'object' ? { ...value } : {};
}

/** Drop the captured query without writing it back. Cancel and unmount use this. */
export function discardBrowseSearch() {
    searchWhenEditorOpened = null;
}

/** Keep the browse query from the moment the editor opens. First open wins. */
export function rememberBrowseSearch() {
    if (searchWhenEditorOpened) {
        return;
    }
    try {
        const $location = getInjector().get('$location');
        if (typeof $location.search !== 'function') {
            return;
        }
        searchWhenEditorOpened = readLocationSearch(() => $location.search());
    } catch {
        // Storybook and unit tests have no Angular injector.
    }
}

function restoreCapturedBrowseSearch() {
    const snapshot = searchWhenEditorOpened;
    searchWhenEditorOpened = null;
    if (!snapshot) {
        return;
    }
    try {
        const injector = getInjector();
        const $location = injector.get('$location');
        if (typeof $location.search !== 'function') {
            return;
        }
        const updates = browseSearchUpdates(readLocationSearch(() => $location.search()), snapshot);
        if (updates.length === 0) {
            return;
        }
        const run = () => {
            for (const [key, value] of updates) {
                $location.search(key, value);
            }
        };
        const $timeout = injector.get('$timeout');
        if (typeof $timeout === 'function') {
            $timeout(run);
        } else {
            run();
        }
    } catch {
        // Storybook and unit tests have no Angular injector.
    }
}

export function categoryFromLocation(): string | undefined {
    try {
        const category = getInjector().get('$location').search()?.category;
        return typeof category === 'string' && category.length > 0 ? category : undefined;
    } catch {
        return undefined;
    }
}

/** Mirror `$location.displayUrl` so the browse shell stays mounted. */
export function publishBrowseUrl(path: string) {
    try {
        const injector = getInjector();
        const $location = injector.get('$location');
        const $timeout = injector.get('$timeout');
        const run = () => {
            if (typeof $location.displayUrl === 'function') {
                $location.displayUrl(path);
            } else {
                $location.path(path);
            }
        };
        if (typeof $timeout === 'function') {
            $timeout(run);
        } else {
            run();
        }
    } catch {
        // Storybook and unit tests have no Angular injector.
    }
}

/** Put the address bar back on `/browse` and restore the query from editor open. */
export function restoreBrowseLocation() {
    try {
        const injector = getInjector();
        const $location = injector.get('$location');
        const path = typeof $location.path === 'function' ? String($location.path() || '') : '';
        if (path !== '/browse' && !path.startsWith('/browse/')) {
            let originalPath: string | undefined;
            try {
                const value = injector.get('$route')?.current?.$$route?.originalPath;
                originalPath = typeof value === 'string' ? value : undefined;
            } catch {
                originalPath = undefined;
            }
            if (keepBrowseShellOnEditorClose(originalPath, path)) {
                publishBrowseUrl('/browse');
            } else {
                const run = () => {
                    $location.path('/browse');
                };
                const $timeout = injector.get('$timeout');
                if (typeof $timeout === 'function') {
                    $timeout(run);
                } else {
                    run();
                }
            }
        }
    } catch {
        // A missing injector must not leave the capture for a later open.
        searchWhenEditorOpened = null;
        return;
    }
    restoreCapturedBrowseSearch();
}

export function primaryKeyFromRow(row: { primaryKey__?: string; entity?: { primaryKey__?: string } } | null | undefined): string | undefined {
    const primaryKey = row?.primaryKey__ ?? row?.entity?.primaryKey__;
    return typeof primaryKey === 'string' && primaryKey.length > 0 ? primaryKey : undefined;
}
